/**
 * Shop System Types
 * Defines Outfitters (buy gear) and Markets (sell fish + quests)
 */

/**
 * Shop Categories - Two distinct types
 */
export enum ShopCategory {
  OUTFITTER = 'outfitter', // Player BUY gear/equipment
  MARKET = 'market',       // Player SELL fish + get quests
}

/**
 * Outfitter subtypes - Shops where player buys gear
 */
export enum OutfitterType {
  TACKLE_SHOP = 'tackle_shop',     // Rods, reels, line, tackle
  GENERAL_STORE = 'general_store', // Bait, consumables, misc
  BOAT_DEALER = 'boat_dealer',     // Boats, boat upgrades
  SPECIALIST = 'specialist',       // Rare/unique gear
}

/**
 * Market subtypes - Places where player sells fish
 */
export enum MarketType {
  CAMP_CHEF = 'camp_chef',         // Casual, low prices, simple quests
  FISH_MARKET = 'fish_market',     // Standard prices, trade quests
  RESTAURANT = 'restaurant',       // Higher prices, quality-focused
  FISHMONGER = 'fishmonger',       // Bulk selling, volume quests
}

/**
 * Base shop definition interface
 */
export interface BaseShopDefinition {
  id: string;
  category: ShopCategory;
  name: string;
  description?: string;
  zoneId: string; // Zone where this shop is located

  // Unlock requirements
  unlockedByDefault: boolean;
  unlockRequirements?: ShopUnlockRequirements;

  // Visual
  icon?: string;
  npcName?: string;                // NPC owner name
  npcDialogue?: string[];          // Flavor text
}

/**
 * Shop unlock requirements
 */
export interface ShopUnlockRequirements {
  minLevel?: number;               // Player level requirement
  minCurrency?: number;            // Must have this much gold
  completedQuests?: string[];      // Quest IDs that must be complete
  totalFishCaught?: number;        // Fish caught milestone
  totalFishSold?: number;          // Fish sold milestone
  buddyCount?: number;             // Must have X buddies
  zoneUnlocked?: string;           // Zone ID must be unlocked
}

/**
 * Outfitter Definition - Shop where player BUYS gear
 */
export interface OutfitterDefinition extends BaseShopDefinition {
  category: ShopCategory.OUTFITTER;
  subtype: OutfitterType;

  // What's for sale
  inventory: OutfitterInventoryItem[];

  // Restock behavior
  restockInterval?: number;        // Hours to restock (0 = never, undefined = always available)
  restockDate?: number;            // Timestamp of last restock
}

/**
 * Item in outfitter's inventory
 */
export interface OutfitterInventoryItem {
  equipmentId: string;             // Equipment definition ID
  price: number;                   // Purchase price
  stock: number;                   // Available quantity (-1 = infinite)
  featured?: boolean;              // Highlighted item
  discount?: number;               // % discount (0.1 = 10% off)
}

/**
 * Market Definition - Place where player SELLS fish and gets quests
 */
export interface MarketDefinition extends BaseShopDefinition {
  category: ShopCategory.MARKET;
  subtype: MarketType;

  // Fish selling prices
  fishPriceMultiplier: number;     // 1.0 = base, 1.2 = +20% more
  acceptedQualities?: Quality[];   // If undefined, accepts all
  acceptedSpecies?: string[];      // If undefined, accepts all

  // Quest board
  questBoard: MarketQuestBoard;
}

/**
 * Market's quest board configuration
 */
export interface MarketQuestBoard {
  maxActiveQuests: number;         // Max quests available at once
  refreshInterval: number;         // Hours between refreshes
  lastRefreshDate?: number;        // Timestamp of last refresh

  // Quest pools
  proceduralQuestPool: string[];   // Procedural quest template IDs
  curatedQuestPool: string[];      // Hand-crafted quest IDs
  buddyQuestPool?: string[];       // Buddy quest template IDs (unlocked with hauler)

  // Procedural weights
  proceduralWeight: number;        // 0-1, chance of procedural vs curated
}

/**
 * Union type for all shop definitions
 */
export type ShopDefinition = OutfitterDefinition | MarketDefinition;

/**
 * Player's shop state (runtime)
 */
export interface PlayerShopState {
  unlockedOutfitters: string[];    // Outfitter IDs
  unlockedMarkets: string[];       // Market IDs
  visitedShops: Record<string, number>; // shopId => last visit timestamp

  // Purchase history (for analytics, recommendations)
  purchaseHistory?: ShopPurchaseRecord[];
}

/**
 * Purchase record
 */
export interface ShopPurchaseRecord {
  shopId: string;
  equipmentId: string;
  price: number;
  purchasedAt: number;
}

import { Quality } from './enums';
